import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PositionInternalDto from '$types/serverTypes/positionInternalDto';
import { useAppDispatch } from '$hooks/use-app-dispatch.hook';
import { singlePositionSlice } from '$store/modules/position/position.reducer';
import { useAppSelector } from '$hooks/use-app-selector.hook';
import { getTenantDefaultFee } from '$constants/SimeraOperations';
import { CalendarModal } from '$components/Employers/CalendarModal/CalendarModal';
import SPKFullProfileIframeModal from '$components/Positions/SPKCandidatesPositionPage/SPKFullProfileIframeModal/SPKFullProfileIframeModal';
import SPKNotAFitConfirmationModal from '$components/Positions/SPKCandidatesPositionPage/SPKNotAFitConfirmationModal/SPKNotAFitConfirmationModal';
import SPKNotAFitFeedbackModal from '$components/Positions/SPKCandidatesPositionPage/SPKNotAFitFeedbackModal/SPKNotAFitFeedbackModal';
import SPKInterviewConfirmationModal from '$components/Positions/SPKCandidatesPositionPage/SPKInterviewConfirmationModal/SPKInterviewConfirmationModal';
import SPKInterviewRequestedSuccessInfoModal from '$components/Positions/SPKCandidatesPositionPage/SPKInterviewRequestedSuccessInfoModal/SPKInterviewRequestedSuccessInfoModal';
import { Virtuoso } from 'react-virtuoso';
import getEmptyResultsText from '$utils/getEmptyResultsText';

import ActiveValues from '../../../constants/ActiveValues';
import EmptyState from '../EmptyState/EmptyState';
import { CandidateData } from '../../../types/candidates';
import SPKEmployerPositionCandidateCard from './SPKEmployerPositionCandidateCard/SPKEmployerPositionCandidateCard';

interface Props {
  activeValue: ActiveValues;
  candidates: CandidateData[];
  position: PositionInternalDto;
  onNextPage?: () => void;
}

const SPKEmployerCandidatesList: FC<Props> = ({
  activeValue,
  candidates,
  position,
  onNextPage,
}) => {
  const [t] = useTranslation('global');
  const dispatch = useAppDispatch();
  const { selectedCandidate, openInterviewCalendarModal } = useAppSelector(
    (state) => state.position
  );

  useEffect(() => {
    dispatch(singlePositionSlice.actions.setAllCandidates(candidates || []));
  }, [candidates, dispatch]);

  const positionFeeAmount = useMemo(
    () => position?.FeeAmount ?? getTenantDefaultFee(position?.Tenant?.Name),
    [position?.FeeAmount, position?.Tenant?.Name]
  );

  if (candidates?.length === 0) {
    const { description, title } = getEmptyResultsText(activeValue, t);
    return (
      <EmptyState
        text={title}
        description={description}
        height="100%"
        transparent
      />
    );
  }

  return (
    <div
      className="flex flex-col gap-4 pb-8 mt-2 h-full max-h-full"
      data-testid="SPKEmployerCandidatesList"
      data-count={candidates.length}
    >
      {/* SPK Full Profile Iframe Modal */}
      <SPKFullProfileIframeModal />

      {/* Not a fit confirmation modal */}
      <SPKNotAFitConfirmationModal />

      {/* Not a Fit Feedback Modal */}
      <SPKNotAFitFeedbackModal positionId={position.Id} />
      {/* Interview Confirmation Modal */}
      <SPKInterviewConfirmationModal />

      {/* Interview calendar modal */}
      {openInterviewCalendarModal &&
        selectedCandidate &&
        selectedCandidate?.CandidatesPositionStatus &&
        'CandidatesPositionStatus' in selectedCandidate && (
          <CalendarModal
            candidateId={selectedCandidate.Id}
            setIsOpen={(value) =>
              dispatch(
                singlePositionSlice.actions.setOpenInterviewCalendarModal(value)
              )
            }
            isOpen={openInterviewCalendarModal}
            candidatePositionStatusId={
              selectedCandidate?.CandidatesPositionStatus?.[0]?.Id
            }
            handleOk={() => {
              dispatch(
                singlePositionSlice.actions.setOpenInterviewCalendarModal(false)
              );
              dispatch(
                singlePositionSlice.actions.setOpenInterviewRequestedModal(true)
              );
            }}
          />
        )}
      {/* Interview  Requested Success Info Modal */}
      <SPKInterviewRequestedSuccessInfoModal />

      {/* {candidates?.map((item) => (
        <SPKEmployerPositionCandidateCard
          key={`PositionCandidate-${item.Id}`}
          candidate={item}
          setSwitchingId={setSwitchingId}
          position={position}
          activeValue={activeValue}
        /> */}
      {candidates && candidates.length > 0 && (
        <div className="h-full max-h-full">
          <Virtuoso
            endReached={onNextPage}
            totalCount={candidates.length}
            style={{
              marginBottom: '10px',
              height: '100%',
            }}
            components={{
              Item: ({ children, ...props }) => (
                <div {...props} className="mb-4 last:mb-0">
                  {children}
                </div>
              ),
            }}
            itemContent={(index: number) => {
              const candidate = candidates[index];
              return (
                <SPKEmployerPositionCandidateCard
                  key={`PositionCandidate-${candidate.Id}`}
                  candidate={candidate}
                  position={position}
                  activeValue={activeValue}
                  feeAmount={positionFeeAmount}
                />
              );
            }}
          />
        </div>
      )}
    </div>
  );
};

SPKEmployerCandidatesList.defaultProps = {
  onNextPage: () => {},
};

export default SPKEmployerCandidatesList;
